import { BaseUserProfile, ILiveStreamOptions } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import {
  KeyboardAvoidingView,
  StyleSheet, Text, View,
} from 'react-native';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import Theme from '../../theme';
import { FormError, TextInput } from '../../ui/components';
import { Switch } from '../../ui/components/Switch';
import { isLiveStreamTopicValid } from '../../utils';
import { defaultScale, i18n } from '../../utils';
import {
  getPreviousBookingStep as getPreviousBookingStepAction,
  liveStreamOptionsNextPressed as liveStreamOptionsNextPressedAction,
  liveStreamOptionToggled as liveStreamOptionToggledAction,
  preWarmupTalkTimeToggled as preWarmupTalkTimeToggledAction,
  willDropOffAfterTalkToggled as willDropOffAfterTalkToggledAction,
} from '../actions';
import {
  BookingFlowFooter,
} from '../components';
import { SelectionContainer } from '../components/SelectionContainer';
import { styles as sharedStyles } from './Styles';

export interface IProps {
  userProfile: BaseUserProfile;
  makeBookingError: string | null;
  handleSubmit: (fn: (values: ILiveStreamOptions) => void) => () => any;
  initialize: (values: {
    preWorkoutTalkTopic: string,
  }) => void;
  livestreamOptions?: ILiveStreamOptions;
  livestreamToggleValue: boolean;
  liveStreamOptionsNextPressed(topic?: string): void;
  getPreviousBookingStep(): void;
  liveStreamOptionToggled(): void;
  preWarmupTalkTimeToggled(): void;
  willDropOffAfterTalkToggled(): void;
  t(key: string, options?: any): string;
}

class LiveStreamOptions extends PureComponent<IProps> {
  private topicRef: any;
  private selectBoxSize: number = defaultScale(135);

  constructor(props) {
    super(props);
  }

  public componentDidMount() {
    const { livestreamOptions, initialize } = this.props;
    if (livestreamOptions?.preWorkoutTalkTopic) {
      initialize({ preWorkoutTalkTopic: livestreamOptions.preWorkoutTalkTopic });
    }
  }

  public render() {
    const {
      t,
      makeBookingError,
      getPreviousBookingStep,
      handleSubmit,
      livestreamToggleValue,
      liveStreamOptionToggled,
      preWarmupTalkTimeToggled,
      livestreamOptions,
      willDropOffAfterTalkToggled,
    } = this.props;

    return (
      <View style={styles.container}>
        <KeyboardAvoidingView behavior='position' keyboardVerticalOffset={100}>
          <Text style={sharedStyles.heading}>{t('liveStreamOptionsStepHeading')}</Text>

          <View style={styles.livestreamSelectContainer}>
            <SelectionContainer
              title={t('liveStreamSelectBoxToggleOn')}
              isSelected={livestreamToggleValue}
              style={{ marginRight: defaultScale(10) }}
              titleStyle={styles.livestreamSelectText}
              onPress={liveStreamOptionToggled}
              width={this.selectBoxSize}
              height={this.selectBoxSize}
              disabled={livestreamToggleValue}
            />

            <SelectionContainer
              title={t('liveStreamSelectBoxToggleOff')}
              isSelected={!livestreamToggleValue}
              style={{ marginLeft: defaultScale(10) }}
              titleStyle={styles.livestreamSelectText}
              onPress={liveStreamOptionToggled}
              width={this.selectBoxSize}
              height={this.selectBoxSize}
              disabled={!livestreamToggleValue}
            />
          </View>

          <View style={styles.livestreamDescriptionContainer}>
            <Text style={styles.livestreamDescriptionText}>{
              livestreamToggleValue
                ? t('liveStreamSelectBoxToggleOnDescription')
                : t('liveStreamSelectBoxToggleOffDescription')
            }</Text>
          </View>
          {livestreamToggleValue &&
            <>
              <View style={[styles.toggleContainer, { marginTop: defaultScale(0) }]}>
                <Text style={styles.toggleLabelText}>{t('liveStreamOption5minTalkToggle')}</Text>
                <Switch
                  onValueChange={preWarmupTalkTimeToggled}
                  value={!!livestreamOptions?.preWorkoutTalk}
                />
              </View>

              {livestreamOptions?.preWorkoutTalk &&
                <>
                  <View style={[styles.toggleContainer, { marginTop: defaultScale(0) }]}>
                    <Text style={styles.toggleLabelText}>{t('liveStreamOptionDropOffAfterTalkToggle')}</Text>
                    <Switch
                      onValueChange={willDropOffAfterTalkToggled}
                      value={!livestreamOptions?.streamerWillDropOffAfterTalk}
                    />
                  </View>
                  <Field
                    trim
                    name='preWorkoutTalkTopic'
                    placeholder={t('liveStreamOptionTopicField')}
                    component={TextInput}
                    returnKeyType='done'
                    height={defaultScale(50)}
                    validate={isLiveStreamTopicValid}
                    ref={this.topicRef}
                    blurOnSumbit
                    isRequired={false}
                  />
                </>
              }
            </>
          }
        </KeyboardAvoidingView>
        <BookingFlowFooter>
          <FormError error={makeBookingError && new Error(makeBookingError)} />

          <View style={styles.actions}>
            <Button
              fontColor={Theme.PRIMARY}
              size={ButtonSize.MEDIUM}
              buttonType={ButtonTypes.OUTLINE}
              title={i18n.t('bookings:back')}
              onPress={getPreviousBookingStep}
              containerStyle={styles.backContainer}
            />

            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              title={i18n.t('bookings:next')}
              onPress={handleSubmit(this.submit)}
              containerStyle={styles.nextContainer}
            />
          </View>
        </BookingFlowFooter>
      </View>
    );
  }

  private submit = ({ preWorkoutTalkTopic }: ILiveStreamOptions) => {
    const { liveStreamOptionsNextPressed } = this.props;
    liveStreamOptionsNextPressed(preWorkoutTalkTopic);
  }
}

const mapStateToProps = (state: IAppStore): Partial<IProps> => ({
  makeBookingError: state.bookings.bookingWizard.makeBookingError,
  livestreamOptions: state.bookings.bookingWizard.livestreamOptions,
  livestreamToggleValue: state.bookings.bookingWizard.livestreamToggleValue,
});

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
  const { userProfile } = ownProps;
  return {
    getPreviousBookingStep: () => dispatch(getPreviousBookingStepAction()),
    liveStreamOptionsNextPressed: (topic?: string) => dispatch(
      liveStreamOptionsNextPressedAction(userProfile, topic),
    ),
    liveStreamOptionToggled: () => dispatch(liveStreamOptionToggledAction()),
    preWarmupTalkTimeToggled: () => dispatch(preWarmupTalkTimeToggledAction()),
    willDropOffAfterTalkToggled: () => dispatch(willDropOffAfterTalkToggledAction()),
  };
};
export const LiveStreamOptionsStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })((reduxForm({ form: 'LiveStreamOptions' })(LiveStreamOptions))),
);

LiveStreamOptionsStep.getTitle = () => <></>;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  actions: {
    flexDirection: 'row',
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 0.7,
    marginLeft: 5,
  },
  toggleContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: defaultScale(20),
  },
  toggleLabelText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  livestreamSelectContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  livestreamSelectText: {
    fontFamily: Theme.textStyle.bodyCopyBold.fontFamily,
    fontSize: Theme.textStyle.bodyCopyBold.fontSize,
  },
  livestreamDescriptionContainer: {
    alignItems: 'center',
    borderBottomColor: Theme.GREY_FIGMA_LIGHT,
    borderBottomWidth: StyleSheet.hairlineWidth,
    padding: defaultScale(20),
    marginBottom: defaultScale(20),
  },
  livestreamDescriptionText: {
    ...Theme.textStyle.bodyCopyReg,
  },
});
